    <template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back previous page" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="compensation" pageId="pay-grade" color="white"></help-link>
                    <v-btn v-if="hasAddRights" fab dark small @click="showAdd" color="primary" title="Add new Pay Grade">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-alert v-if="error" :value="true" color="error" icon="error" outline style="margin:10px 0px 10px 0px;">
                Error encountered. Please contact support. Error details: {{ errorMessage }}
            </v-alert> 
            <v-card class="elavation-1 mt-5"> 
                <v-toolbar flat height="45px">
                    <span class="font-weight-bold ">Pay Grades</span>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile?headers_mobile:headers"
                    :items="pay_grades"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile" @click="showEdit(props.item)">
                            <td>{{props.item.code}} - {{props.item.role}}</td>
                            <td>{{numFormat.formatNumber(props.item.salaryStart)}} to {{numFormat.formatNumber(props.item.salaryEnd)}}</td>
                        </tr>

                        <tr v-else @click="showEdit(props.item)" :title="hasEditRights?'Click to Edit details':''" :class="hasEditRights?'clickable':''">
                            <td>{{props.item.code}}</td>
                            <td>{{props.item.role}}</td>
                            <td>{{props.item.description}}</td>
                            <td>{{numFormat.formatNumber(props.item.salaryStart)}}</td>
                            <td>{{numFormat.formatNumber(props.item.salaryEnd)}}</td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>

            <!-- Comments and Updates -->
            <v-layout row wrap>
                <v-flex xs12 sm6>
                    <!-- comments history component -->
                    <comments-history v-if="!loading" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading" ref="updateHistoryRef" :page_query_id="pageQueryId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>

        <v-dialog v-model="dialogAddEdit" persistent width="700">
            <add-edit ref="addEditRef" :dialogAddEdit.sync="dialogAddEdit" @send="getPayGrades"></add-edit>
        </v-dialog>

        <!-- status messaging component -->
        <status-messaging :page_query_id="pageQueryId" @refresh="updateHandler"></status-messaging>
    </v-layout> 
</template>

<script>

import payGradeApi from '@/api/paygrade'
import numFormat from '@/common/num_format'
import pageKeys from '@/common/pagekeys'
import notificationTypes from '@/common/notificationTypes'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'
import { hasAddRights, hasEditRights } from '@/helpers/access-permissions'

export default {     
    name: 'PayGrade',
    metaInfo: {
        title: 'Pay Grades',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View, create company Pay grades' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-edit': () => import('@/components/PayGrades/AddEditPayGrade'),
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory')
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)

        return {
            numFormat,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            error: false,
            errorMessage: '',
            loading: false,
            expand: false,
            pay_grades: [],
            headers: [
                {text: 'Code',align: 'left',value: 'code',width: '10%', sortable:true},
                {text: 'Role',align: 'left',value: 'role',width: '10%', sortable:true},
                {text: 'Description',align: 'left',value: 'description',width: '10%', sortable:true},
                {text: 'Salary Start',align: 'left',value: 'salaryStart',width: '10%', sortable:true},
                {text: 'Salary End',align: 'left',value: 'salaryEnd',width: '10%', sortable:true},
            ],
            headers_mobile: [
                {text: 'Code & Role',align: 'left',value: 'role',width: '10%', sortable:true},
                {text: 'Salary Range',align: 'left',value: 'salaryStart',width: '10%', sortable:true},
            ],
            dialogAddEdit: false,
        }
    },
    methods: {  
        updateHandler(data) {
            if (data!=this.$store.state.userId) {
                this.getPayGrades(false)
            }
            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        },  
        showEdit(data) {
            if (this.hasEditRights) {
                this.$refs.addEditRef.setUpdate(data)
                this.dialogAddEdit = true
            }
        },
        showAdd() {
            this.$refs.addEditRef.setAdd()
            this.dialogAddEdit = true
        },
        async getPayGrades(show_loading) {
            try  {
                this.loading = show_loading
                const response = await payGradeApi.getByCompany()
                if (response && response.status == 200) {
                    this.pay_grades = response.data.items
                }
                this.loading = false
            }catch(error) {
                this.error = true
                this.errorMessage = error
            }
        }
    },
    created() {   
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Pay Grades'
        })

        this.getPayGrades(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        companyIdStr() {
            return this.$store.state.companyId
        },
        pageQueryId() {
            return `${this.companyIdStr}_paygrades`
        },
        commentsData() {
            if (!this.loading) {
                return {
                    pageQueryId: this.pageQueryId,
                    pageItemCreatedBy: null,
                    pageLink: this.$router.currentRoute.path,
                    pageKey: pageKeys.paygrades,
                    notificationTypeId: notificationTypes.paygrades
                }
            }
        },
        isMobile() {
            return window.innerWidth < 600
        },
    }
}
</script>